import type { AgentHandoffSession } from "@/types/agentHandoff";

const STORAGE_PREFIX = "agent-handoff:";
const INDEX_KEY = "agent-handoff:index";
const MAX_SESSIONS = 20;
const TTL_MS = 24 * 60 * 60 * 1000;

function storageKey(id: string): string {
  return `${STORAGE_PREFIX}${id}`;
}

function readIndex(): string[] {
  if (typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(INDEX_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeIndex(ids: string[]): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(INDEX_KEY, JSON.stringify(ids.slice(0, MAX_SESSIONS)));
}

function pruneExpired(): void {
  const ids = readIndex();
  const kept: string[] = [];
  const now = Date.now();
  for (const id of ids) {
    const session = getHandoffSession(id);
    if (session && now - session.createdAt < TTL_MS) {
      kept.push(id);
    } else {
      sessionStorage.removeItem(storageKey(id));
    }
  }
  writeIndex(kept);
}

export function createHandoffSession(
  data: Omit<AgentHandoffSession, "id" | "createdAt">
): AgentHandoffSession {
  pruneExpired();
  const id = `handoff-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const session: AgentHandoffSession = {
    ...data,
    id,
    createdAt: Date.now(),
  };
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(storageKey(id), JSON.stringify(session));
    const ids = [id, ...readIndex().filter((x) => x !== id)];
    writeIndex(ids);
  }
  return session;
}

export function getHandoffSession(id: string): AgentHandoffSession | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(storageKey(id));
    if (!raw) return null;
    const session = JSON.parse(raw) as AgentHandoffSession;
    if (Date.now() - session.createdAt > TTL_MS) {
      clearHandoffSession(id);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function updateHandoffSession(
  id: string,
  patch: Partial<AgentHandoffSession>
): AgentHandoffSession | null {
  const existing = getHandoffSession(id);
  if (!existing) return null;
  const updated = { ...existing, ...patch, id: existing.id, createdAt: existing.createdAt };
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(storageKey(id), JSON.stringify(updated));
  }
  return updated;
}

export function clearHandoffSession(id: string): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(storageKey(id));
  writeIndex(readIndex().filter((x) => x !== id));
}
